import { useState } from "react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { PromptType, SECTOR_DISPLAY_NAMES } from "../../types/market-recap";
import { getPromptConfig } from "./promptConfigs";
import { isValidTicker } from "./messageConstructor";
import { cn } from "../../lib/utils";

interface VariablesStepProps {
  promptType: PromptType;
  variables: Record<string, string | string[]>;
  onChange: (name: string, value: string | string[]) => void;
}

export function VariablesStep({
  promptType,
  variables,
  onChange,
}: VariablesStepProps) {
  const config = getPromptConfig(promptType);
  const [tickerError, setTickerError] = useState<string | null>(null);

  if (!config) return null;

  const handleTickerChange = (value: string) => {
    const upperValue = value.toUpperCase();
    onChange("ticker", upperValue);

    if (value && !isValidTicker(value)) {
      setTickerError("Enter 1-5 letters (e.g., AAPL)");
    } else {
      setTickerError(null);
    }
  };

  const handleIndicesChange = (index: string, checked: boolean) => {
    const currentIndices = (variables.indices as string[]) || [];
    if (checked) {
      onChange("indices", [...currentIndices, index]);
    } else {
      onChange(
        "indices",
        currentIndices.filter((i) => i !== index)
      );
    }
  };

  const renderVariable = (variable: (typeof config.variables)[0]) => {
    const value = variables[variable.name];

    switch (variable.type) {
      case "ticker":
        return (
          <div className="space-y-2" key={variable.name}>
            <Label htmlFor={variable.name}>{variable.label}</Label>
            <Input
              id={variable.name}
              placeholder={variable.placeholder}
              value={(value as string) || ""}
              onChange={(e) => handleTickerChange(e.target.value)}
              className={cn(
                "uppercase",
                tickerError && "border-destructive focus-visible:ring-destructive"
              )}
              maxLength={5}
            />
            {tickerError && (
              <p className="text-sm text-destructive">{tickerError}</p>
            )}
          </div>
        );

      case "period":
        return (
          <div className="space-y-2" key={variable.name}>
            <Label htmlFor={variable.name}>{variable.label}</Label>
            <Select
              value={(value as string) || (variable.default as string)}
              onValueChange={(v) => onChange(variable.name, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                {variable.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "direction":
        return (
          <div className="space-y-2" key={variable.name}>
            <Label htmlFor={variable.name}>{variable.label}</Label>
            <Select
              value={(value as string) || (variable.default as string)}
              onValueChange={(v) => onChange(variable.name, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select direction" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="gainers">Top Gainers</SelectItem>
                <SelectItem value="losers">Top Losers</SelectItem>
                <SelectItem value="both">Both Gainers & Losers</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );

      case "count":
        return (
          <div className="space-y-2" key={variable.name}>
            <Label htmlFor={variable.name}>{variable.label}</Label>
            <Select
              value={(value as string) || (variable.default as string)}
              onValueChange={(v) => onChange(variable.name, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select count" />
              </SelectTrigger>
              <SelectContent>
                {variable.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    Top {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "sector":
        return (
          <div className="space-y-2" key={variable.name}>
            <Label htmlFor={variable.name}>{variable.label}</Label>
            <Select
              value={(value as string) || (variable.default as string)}
              onValueChange={(v) => onChange(variable.name, v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select sector" />
              </SelectTrigger>
              <SelectContent>
                {variable.options?.map((option) => (
                  <SelectItem key={option} value={option}>
                    {SECTOR_DISPLAY_NAMES[
                      option as keyof typeof SECTOR_DISPLAY_NAMES
                    ] || option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case "indices":
        const selectedIndices = (value as string[]) || (variable.default as string[]) || [];
        return (
          <div className="space-y-3" key={variable.name}>
            <Label>{variable.label}</Label>
            <div className="grid grid-cols-2 gap-3">
              {variable.options?.map((option) => (
                <label
                  key={option}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                    selectedIndices.includes(option)
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-primary/50"
                  )}
                >
                  <Checkbox
                    checked={selectedIndices.includes(option)}
                    onCheckedChange={(checked) =>
                      handleIndicesChange(option, checked as boolean)
                    }
                  />
                  <span className="text-sm font-medium">{option}</span>
                </label>
              ))}
            </div>
            {selectedIndices.length === 0 && (
              <p className="text-sm text-destructive">
                Select at least one index
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-foreground">
          Fill in the details
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Provide the specific information for your {config.name.toLowerCase()}
        </p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        {config.variables.map(renderVariable)}
      </div>
    </div>
  );
}
